/**
 * Sessions Resource
 *
 * Thin 1:1 mapping to the sessions API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import { APIError, SDKError, SessionImageExpiredError } from '../errors.js';
import type { HttpLayer } from '../http.js';
import type { BinaryContent } from '../types/media.js';
import type {
  Session,
  CreateSessionRequest,
  UpdateSessionRequest,
  Message,
  CreateMessageRequest,
  SessionEvent,
  SubscribeSessionEventsRequest,
  UnsubscribeSessionEventsRequest,
  EventPayloadPage,
  EventPayloadWindowEntry,
  ReadEventPayloadsRequest,
  ContextEntryRef,
  ReadContextEntriesRequest,
  ContextEntryRead,
  HydratedEventData,
  HydratedEventWindowEntry,
  Checkpoint,
  SessionImageUpload,
  SessionQueue,
  SessionTasks,
  EditQueueItemRequest,
  UserConfig,
  UpdateUserConfigRequest,
} from '../types/index.js';

// `offset_bytes` is a BYTE offset: the server slices the event's `data` TEXT
// rune-safely and echoes the byte-start it used. The paged-read cursor must
// advance by the UTF-8 BYTE length of each returned fragment — NOT its string
// `.length`, which counts UTF-16 code units and desyncs the offset the moment a
// payload contains any multibyte content (emoji, non-Latin text), mis-slicing
// every subsequent page.
const payloadByteLength = (s: string): number => new TextEncoder().encode(s).length;

export class SessionsResource {
  constructor(private http: HttpLayer) {}

  private prefix(workspaceId?: string | null): string {
    return workspaceId ? `/v1/sessions/${workspaceId}` : `/v1/sessions`;
  }

  // Image upload/fetch live on the reasoning routes, beside send-message.
  private reasoningPrefix(workspaceId?: string | null): string {
    return workspaceId ? `/v1/reasoning/${workspaceId}` : `/v1/reasoning`;
  }

  // --- Sessions ---

  async create(
    workspaceId: string | null | undefined,
    params: CreateSessionRequest,
  ): Promise<Session> {
    return this.http.post(`${this.prefix(workspaceId)}/sessions`, params);
  }

  /**
   * Sessions for this lane, most recent transcript message first.
   *
   * The route is BOUNDED: `limit` defaults to 200 server-side and caps at 500,
   * and ordering is `(last_accessed DESC, id DESC)` so `offset` is stable
   * across pages. The response stays a bare array, so to know whether the bound
   * cut anything off, ask for one more row than you intend to render — a
   * returned length greater than your page size is the signal, and is exact at
   * the boundary in a way `length === limit` is not.
   *
   * @param workspaceId Workspace to scope to, or null/undefined for the
   *   account-wide list (both lanes).
   * @param params Page bounds: `limit` (1-500) and `offset` (>= 0).
   */
  async list(
    workspaceId: string | null | undefined,
    params?: { limit?: number; offset?: number },
  ): Promise<Session[]> {
    return this.http.get(`${this.prefix(workspaceId)}/sessions`, params);
  }

  async get(workspaceId: string | null | undefined, sessionId: string): Promise<Session> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}`,
    );
  }

  async getMostRecent(workspaceId: string | null | undefined): Promise<Session> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/most-recent`,
    );
  }

  async update(
    workspaceId: string | null | undefined,
    sessionId: string,
    params: UpdateSessionRequest,
  ): Promise<Session> {
    return this.http.patch(
      `${this.prefix(workspaceId)}/sessions/${sessionId}`,
      params,
    );
  }

  async delete(workspaceId: string | null | undefined, sessionId: string): Promise<void> {
    return this.http.delete(
      `${this.prefix(workspaceId)}/sessions/${sessionId}`,
    );
  }

  // --- Tasks ---

  /**
   * Read a session's task list: the tasks its agents keep, in order, with the
   * session's Keep working state. Tasks are changed only by agents.
   *
   * @param workspaceId Workspace for a workspace-attached session; null for a workspace-less one.
   * @param sessionId The session whose tasks to read.
   */
  async listTasks(workspaceId: string | null | undefined, sessionId: string): Promise<SessionTasks> {
    return this.http.get(`${this.reasoningPrefix(workspaceId)}/sessions/${sessionId}/tasks`);
  }

  /**
   * Turn a session's Keep working on or off. While it is on, the main agent's
   * work is checked against the task list each time it would stop, and a
   * message saying what is unfinished keeps it going. Either setting clears a pause.
   * Turning it on starts nothing by itself; work resumes with the next message.
   *
   * @param workspaceId Workspace for a workspace-attached session; null for a workspace-less one.
   * @param sessionId The session to change.
   * @param on `true` to turn Keep working on, `false` to turn it off.
   */
  async setKeepWorking(workspaceId: string | null | undefined, sessionId: string, on: boolean): Promise<SessionTasks> {
    return this.http.put(`${this.reasoningPrefix(workspaceId)}/sessions/${sessionId}/tasks/keep-working`, { on });
  }

  // --- Queue (sessions ADR-d28b1) ---

  /**
   * Read a session's queue: the messages waiting to go to the agent once the
   * running turn ends, in send order, with each item's full text.
   *
   * @param workspaceId Workspace for a workspace-attached session; null for a workspace-less one.
   * @param sessionId The session whose queue to read.
   * @param options `participantId` reads that participant's user queue (a subagent tab's); omitted, the primary's.
   */
  async listQueue(
    workspaceId: string | null | undefined,
    sessionId: string,
    options?: { participantId?: string },
  ): Promise<SessionQueue> {
    return this.http.get(`${this.reasoningPrefix(workspaceId)}/sessions/${sessionId}/queue`, {
      participant_id: options?.participantId,
    });
  }

  /**
   * Replace a queued message's text; it keeps its place in the queue. Fails
   * with `queue_item_gone` once the message has started.
   *
   * @param workspaceId Workspace for a workspace-attached session; null for a workspace-less one.
   * @param sessionId The session that owns the queue.
   * @param itemId The queue item to edit.
   * @param params The new text and the `@` references it carries.
   * @param options `participantId` edits in that participant's user queue (a subagent tab's); omitted, the primary's.
   */
  async editQueueItem(
    workspaceId: string | null | undefined,
    sessionId: string,
    itemId: string,
    params: EditQueueItemRequest,
    options?: { participantId?: string },
  ): Promise<SessionQueue> {
    return this.http.patch(`${this.reasoningPrefix(workspaceId)}/sessions/${sessionId}/queue/${itemId}${participantQuery(options)}`, params);
  }

  /**
   * Remove one queued message. Fails with `queue_item_gone` once it has started.
   *
   * @param workspaceId Workspace for a workspace-attached session; null for a workspace-less one.
   * @param sessionId The session that owns the queue.
   * @param itemId The queue item to remove.
   * @param options `participantId` removes from that participant's user queue (a subagent tab's); omitted, the primary's.
   */
  async removeQueueItem(
    workspaceId: string | null | undefined,
    sessionId: string,
    itemId: string,
    options?: { participantId?: string },
  ): Promise<SessionQueue> {
    return this.http.delete(`${this.reasoningPrefix(workspaceId)}/sessions/${sessionId}/queue/${itemId}`, {
      participant_id: options?.participantId,
    });
  }

  /**
   * Move a queued message one place up or down. Fails with `queue_item_gone`
   * once it has started.
   *
   * @param workspaceId Workspace for a workspace-attached session; null for a workspace-less one.
   * @param sessionId The session that owns the queue.
   * @param itemId The queue item to move.
   * @param direction `up` toward the head of the queue, `down` toward its tail.
   * @param options `participantId` moves within that participant's user queue (a subagent tab's); omitted, the primary's.
   */
  async moveQueueItem(
    workspaceId: string | null | undefined,
    sessionId: string,
    itemId: string,
    direction: 'up' | 'down',
    options?: { participantId?: string },
  ): Promise<SessionQueue> {
    return this.http.post(`${this.reasoningPrefix(workspaceId)}/sessions/${sessionId}/queue/${itemId}/move${participantQuery(options)}`, { direction });
  }

  /**
   * Remove every message waiting on a session's queue.
   *
   * @param workspaceId Workspace for a workspace-attached session; null for a workspace-less one.
   * @param sessionId The session whose queue to clear.
   * @param options `participantId` clears that participant's user queue (a subagent tab's); omitted, the primary's.
   */
  async clearQueue(
    workspaceId: string | null | undefined,
    sessionId: string,
    options?: { participantId?: string },
  ): Promise<SessionQueue> {
    return this.http.delete(`${this.reasoningPrefix(workspaceId)}/sessions/${sessionId}/queue`, {
      participant_id: options?.participantId,
    });
  }

  // --- Messages ---

  async createMessage(
    workspaceId: string | null | undefined,
    sessionId: string,
    params: CreateMessageRequest,
  ): Promise<Message> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/messages`,
      params,
    );
  }

  async getMessages(
    workspaceId: string | null | undefined,
    sessionId: string,
  ): Promise<Message[]> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/messages`,
    );
  }

  /**
   * Keyset-paged transcript read. Exactly one window mode per call:
   *
   *   - `{ latest: true, limit }` — the newest `limit` visible messages,
   *     ascending (the tail page).
   *   - `{ beforeMessageIndex, limit }` — up to `limit` visible messages
   *     below `beforeMessageIndex`, ascending (backward extension).
   *   - `{ afterMessageIndex, limit }` — up to `limit` visible messages
   *     above `afterMessageIndex`, ascending (live-tail catch-up).
   *
   * Every row carries `message_index` — the lane's own monotonic cursor the
   * caller forms the next page from. Windowing params require `limit > 0`
   * (422 otherwise); the unparameterized {@link getMessages} stays the
   * byte-compatible full read.
   *
   * `participantId` reads that participant's conversation; omitted, the
   * primary's (the chat transcript).
   */
  async getMessagesPage(
    workspaceId: string | null | undefined,
    sessionId: string,
    opts: (
      | { latest: true; limit: number }
      | { beforeMessageIndex: number; limit: number }
      | { afterMessageIndex: number; limit: number }
    ) & { participantId?: string },
  ): Promise<Message[]> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/messages`,
      {
        participant_id: opts.participantId,
        latest: 'latest' in opts ? opts.latest : undefined,
        before_message_index:
          'beforeMessageIndex' in opts ? opts.beforeMessageIndex : undefined,
        after_message_index:
          'afterMessageIndex' in opts ? opts.afterMessageIndex : undefined,
        limit: opts.limit,
      },
    );
  }

  async clearMessages(
    workspaceId: string | null | undefined,
    sessionId: string,
  ): Promise<void> {
    return this.http.delete(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/messages`,
    );
  }

  async editMessageAndRewind(
    workspaceId: string | null | undefined,
    sessionId: string,
    messageId: string,
    content: string,
  ): Promise<Message[]> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/messages/${messageId}/edit-and-rewind`,
      { content },
    );
  }

  // --- Images ---

  /**
   * Upload one image to a session and get back its `image://` handle.
   *
   * The bytes go up as the request body under the blob's own content type — the
   * server SNIFFS them, so a declared type is advisory and a lying one is
   * corrected in the response. Server-side the upload refuses a non-image
   * (415), an animated GIF (415), a body over the 5 MiB per-image cap (413), a
   * header declaring more than 40 megapixels (413), and anything past the
   * session's storage ceilings (413); it strips EXIF/GPS/colour profiles by
   * re-encoding into the read-quality copy the vision model reads.
   *
   * Pass the returned `image_id` to `ChatSession.send` as
   * `{ type: 'image', image_id, label }` to attach it to a message. Nothing
   * image-shaped is persisted: the read-quality copy expires from the cache,
   * and the message keeps a text placeholder.
   *
   * The routes live under the reasoning prefix, not the sessions prefix,
   * because attachment binding is part of the send path.
   *
   * @param sessionId Session to attach the image to.
   * @param bytes The image itself (a File, a Blob, or raw bytes).
   * @param label User-facing name; neutralized and length-capped server-side.
   * @param workspaceId Workspace for a workspace-attached session; omit for a
   *   workspace-less (web) session.
   * @param source How the user added it — `paste` or `upload`.
   * @param resupplyImageId Restore the expired read copy of this existing image
   *   for a blocked queued message; the bytes must be the image it attached.
   */
  async uploadImage(
    sessionId: string,
    bytes: Blob,
    label: string,
    workspaceId?: string | null,
    source?: 'paste' | 'upload',
    resupplyImageId?: string,
  ): Promise<SessionImageUpload> {
    if (bytes.type === '') {
      throw new SDKError(
        'uploadImage: the blob carries no content type — construct it with an explicit image/* type',
      );
    }
    return this.http.postBinary<SessionImageUpload>(
      `${this.reasoningPrefix(workspaceId)}/sessions/${sessionId}/images`,
      bytes,
      bytes.type,
      {
        // Percent-encoded so a Unicode filename survives an ASCII header and a
        // CR/LF the server's neutralizer must strip is expressible at all.
        'X-Image-Label': encodeURIComponent(label),
        'X-Image-Source': source ?? 'upload',
        ...(resupplyImageId ? { 'X-Image-Resupply': resupplyImageId } : {}),
      },
    );
  }

  /**
   * Read an attached image's bytes for display (its read-quality copy) with
   * the stored content type.
   *
   * @param sessionId Session the image is attached to.
   * @param imageId Image id from the upload response or an `image://` handle.
   * @param options.workspaceId Workspace for a workspace-attached session; omit
   *   for a workspace-less (web) session.
   * @throws SessionImageExpiredError when the read copy has expired.
   */
  async imageContent(
    sessionId: string,
    imageId: string,
    options: { workspaceId?: string | null } = {},
  ): Promise<BinaryContent> {
    try {
      return await this.http.get(
        `${this.reasoningPrefix(options.workspaceId)}/sessions/${sessionId}/images/${imageId}/content`,
        undefined,
        { acceptBinaryWithType: true },
      );
    } catch (err) {
      if (err instanceof APIError && err.code === 'image_expired') {
        throw new SessionImageExpiredError(imageId);
      }
      throw err;
    }
  }

  // --- Checkpoints ---

  async listCheckpoints(
    workspaceId: string | null | undefined,
    sessionId: string,
  ): Promise<Checkpoint[]> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/checkpoints`,
    );
  }

  async createCheckpoint(
    workspaceId: string | null | undefined,
    sessionId: string,
  ): Promise<Checkpoint> {
    // Send an empty body — the create-checkpoint route declares a (required)
    // body whose only field (agent_state) is optional; without a body Huma
    // rejects with "request body is required".
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/checkpoints`,
      {},
    );
  }

  async getCheckpoint(
    workspaceId: string | null | undefined,
    checkpointId: string,
  ): Promise<Checkpoint> {
    return this.http.get(
      `${this.prefix(workspaceId)}/checkpoints/${checkpointId}`,
    );
  }

  async deleteCheckpoint(
    workspaceId: string | null | undefined,
    checkpointId: string,
  ): Promise<void> {
    return this.http.delete(
      `${this.prefix(workspaceId)}/checkpoints/${checkpointId}`,
    );
  }

  async restoreCheckpoint(
    workspaceId: string | null | undefined,
    sessionId: string,
    checkpointId: string,
  ): Promise<void> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/checkpoints/${checkpointId}/restore`,
    );
  }

  // --- User Config ---

  async getUserConfig(
    workspaceId: string | null | undefined,
    sessionId: string,
  ): Promise<UserConfig> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/user-config`,
    );
  }

  async updateUserConfig(
    workspaceId: string | null | undefined,
    sessionId: string,
    params: UpdateUserConfigRequest,
  ): Promise<UserConfig> {
    return this.http.patch(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/user-config`,
      params,
    );
  }

  async getUserConfigById(
    workspaceId: string | null | undefined,
    userConfigId: string,
  ): Promise<UserConfig> {
    return this.http.get(
      `${this.prefix(workspaceId)}/user-configs/${userConfigId}`,
    );
  }

  // --- Events ---

  async getEvents(
    workspaceId: string | null | undefined,
    sessionId: string,
    afterSequence?: number,
    limit?: number,
    opts?: {
      /** Newest `limit` events, ascending. Combines with neither cursor. */
      latest?: boolean;
      /** The `limit` events immediately below this sequence, ascending. */
      beforeSequence?: number;
      /** Heavy-stripped rows with handle + size_bytes above the inline threshold. */
      skeleton?: boolean;
    },
  ): Promise<SessionEvent[]> {
    return this.http.get(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/events`,
      {
        after_sequence: afterSequence,
        limit,
        latest: opts?.latest,
        before_sequence: opts?.beforeSequence,
        skeleton: opts?.skeleton,
      },
    );
  }

  // --- Lazy event-payload hydration ---

  /**
   * Hydrate ONE skeletonized event's full `data`, looping `has_more` to
   * completion so the returned object is the COMPLETE, byte-identical
   * event data — never a partial page. The server pages the event's `data` TEXT
   * rune-safely; this reassembles the fragments and JSON-parses the whole.
   *
   * Rejects loudly on a non-2xx (404 → the event is no longer available in the
   * caller's lane; the caller maps that to hydrationError='not_available').
   * A page whose reassembled text fails to parse is a loud error, not a silent
   * empty (no-fallbacks).
   */
  async readEventPayload(
    workspaceId: string | null | undefined,
    sessionId: string,
    sequenceNum: number,
    maxBytes?: number,
  ): Promise<HydratedEventData> {
    const base = `${this.prefix(workspaceId)}/sessions/${sessionId}/events/${sequenceNum}/payload`;
    let text = '';
    let offset = 0;
    // Loop pages until the server reports the payload is fully drained. The
    // server bounds forward progress (each page returns >= 1 byte until the
    // end), so this terminates.
    for (;;) {
      const page: EventPayloadPage = await this.http.get(base, {
        offset_bytes: offset,
        max_bytes: maxBytes,
      });
      text += page.content;
      if (!page.has_more) break;
      const next = page.offset_bytes + payloadByteLength(page.content);
      if (next <= offset) {
        // Defensive: no forward progress would loop forever — fail loudly
        // rather than hang (no-fallbacks / no silent truncation).
        throw new Error(
          `readEventPayload: no forward progress at offset ${offset} (seq ${sequenceNum})`,
        );
      }
      offset = next;
    }
    return JSON.parse(text) as HydratedEventData;
  }

  /**
   * Hydrate a WINDOW of events in one request (one server query). Each
   * returned entry is a byte-bounded first page: a COMPLETE entry (`has_more`
   * false) is parsed to `data`; an incomplete one carries `data: null` +
   * `has_more: true` and the caller completes it with {@link readEventPayload}.
   * `sequenceNums` is server-capped — an over-cap request rejects 422.
   */
  async readEventPayloads(
    workspaceId: string | null | undefined,
    sessionId: string,
    sequenceNums: number[],
    maxBytes?: number,
  ): Promise<HydratedEventWindowEntry[]> {
    const body: ReadEventPayloadsRequest = { sequence_nums: sequenceNums, max_bytes: maxBytes };
    const res: { payloads: EventPayloadWindowEntry[] } = await this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/events/payloads`,
      body,
    );
    return res.payloads.map((p) => ({
      sequence_num: p.sequence_num,
      data: p.has_more ? null : (JSON.parse(p.content) as HydratedEventData),
      has_more: p.has_more,
    }));
  }

  // --- Context-entry reads ---

  /**
   * Read history entries by reference — the `(task_id, message_index)` pairs a
   * `context_rag_completed` event lists as `hit_refs`. Each entry comes back
   * exactly as the run-start retrieval seed inlined it for the model: an entry
   * longer than the read-default size is cut to that first page and flagged
   * `excerpt: true`, with `total_bytes` naming the full size.
   *
   * Entries return in request order. A ref with no entry in this session
   * returns `found: false`. The read writes nothing to the session. `refs` is
   * server-capped — an over-cap request rejects 422.
   */
  async readContextEntries(
    workspaceId: string | null | undefined,
    sessionId: string,
    refs: ContextEntryRef[],
  ): Promise<ContextEntryRead[]> {
    const body: ReadContextEntriesRequest = { refs };
    const res: { entries: ContextEntryRead[] } = await this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/context-entries/read`,
      body,
    );
    return res.entries;
  }

  // --- Session-event subscription ---

  /**
   * Register the caller's live bridge connection as a `SessionScope` fanout
   * target for `sessionId` and trigger a replay of persisted events after
   * `params.after_sequence`. Server-managed subscription (no client-sent bridge
   * frame, ADR-003): this HTTP action writes the `bridge_subscriptions` row as a
   * side effect. Rejects loudly on a non-2xx (403 device-ownership / 404 session /
   * 422 bad cursor). Workspace-scoped when a workspaceId is supplied, else the
   * workspace-less sibling route.
   */
  async subscribeSessionEvents(
    workspaceId: string | null | undefined,
    sessionId: string,
    params: SubscribeSessionEventsRequest,
  ): Promise<void> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/subscribe`,
      params,
    );
  }

  /**
   * Delete the `(device_id, "session", sessionId)` subscription row. Runs the
   * device-ownership check of {@link subscribeSessionEvents} (a caller may only
   * unsubscribe a device that is provably their own live connection) and
   * succeeds when the session no longer exists, since its subscription is
   * already gone. Rejects loudly on a non-2xx.
   */
  async unsubscribeSessionEvents(
    workspaceId: string | null | undefined,
    sessionId: string,
    params: UnsubscribeSessionEventsRequest,
  ): Promise<void> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/unsubscribe`,
      params,
    );
  }
}

function participantQuery(options?: { participantId?: string }): string {
  return options?.participantId ? `?participant_id=${encodeURIComponent(options.participantId)}` : '';
}
